import { circlePinGreenIcon, dragPanIcon, gearIcon, circlePlusPlaceholderIcon, closeIcon } from '../../utils/images';
import './LocationMap.scss';
import { useState } from 'react';
import { Location } from '../../types/Locations';
import { AddLocation, addLocation, deleteLocation, DeleteLocation } from '../../services/locationData';
import { BigDisplay } from '../../types/BigDisplay';

interface Screen {
  id: string;
  screenSize: string;
  location_code?: string;
  // Add other screen properties as needed
}

interface Sensor {
  id: string;
  sensor_type: string;
  location_code?: string;
  // Add other sensor properties as needed
}

interface LocationMapProps {
  locations: Location[];
  screens: Screen[];
  displays: BigDisplay[];
  sensors: Sensor[];
  storeId: string;
  onLocationAdded?: () => void; // Callback to refresh data
}

export const LocationMap: React.FC<LocationMapProps> = ({ locations, screens, displays, sensors, storeId, onLocationAdded }) => {
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [selectedLocationId, setSelectedLocationId] = useState<number>();
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [clickedPlaceholder, setClickedPlaceholder] = useState<string | null>(null);
  const [newLocationCode, setNewLocationCode] = useState('');
  const [suggestedLocationCode, setSuggestedLocationCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredLocations = (listType: string) => {
    return locations.filter(location => location.location_code.includes(listType));
  };

  const handleAddLocationClick = (suggestedCode: string) => {
    setSuggestedLocationCode(suggestedCode);
    setNewLocationCode(suggestedCode);
    setClickedPlaceholder(suggestedCode);
    setShowAddLocationModal(true);
  };

  const handleAddLocation = async () => {
    if (!newLocationCode.trim()) {
      alert('Please enter a location code');
      return;
    }

    setIsSubmitting(true);
    try {
      const addData: AddLocation = {
        store_id: storeId,
        location_code: newLocationCode.trim()
      };
      
      await addLocation(addData);
      
      // Close modal and reset form   
      setShowAddLocationModal(false);
      setClickedPlaceholder(null);
      setNewLocationCode('');
      setSuggestedLocationCode('');
      
      // Call the callback to refresh data
      if (onLocationAdded) {
        onLocationAdded();
      }
    } catch (error) {
      console.error('Error adding location:', error);
      alert('Failed to add location. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelAddLocation = () => {
    setShowAddLocationModal(false);
    setClickedPlaceholder(null);
    setNewLocationCode('');
    setSuggestedLocationCode('');
  };

  const handleDeleteLocation = async () => {
    if (!selectedLocationId) {
      alert('No location selected for deletion.');
      return;
    }

    setIsSubmitting(true);
    try {
      const deleteData: DeleteLocation = {
        id: selectedLocationId,
        store_id: storeId,
        location_code: selectedLocation
      };
      
      await deleteLocation(deleteData);
      
      // Reset selection
      setSelectedLocation('');
      setSelectedLocationId(undefined);
      
      // Call the callback to refresh data after successful deletion
      if (onLocationAdded) {
        onLocationAdded();
      }
      
    } catch (error) { 
      console.error('Error deleting location:', error);
      alert('Failed to delete location. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  // Pre-calculate filtered locations to avoid function calls during render
  const locationsA = filteredLocations('A');
  const locationsB = filteredLocations('B');
  const locationsC = filteredLocations('C');

  const handlePinClick = (locationCode: string, locationId: number) => {
    setSelectedLocation(selectedLocation === locationCode ? '' : locationCode);
    setSelectedLocationId(selectedLocationId === locationId ? 0 : locationId);
  };

  const getLocationScreens = (locationCode: string) => {
    return screens.filter(screen => screen.location_code === locationCode);
  };

  const getLocationDisplays = (locationCode: string) => {
    return displays.filter(display => display.location_code === locationCode);
  };

  const getLocationSensors = (locationCode: string) => {
    return sensors.filter(sensor => sensor.location_code === locationCode);
  };

  const renderLocationList = (filteredLocationList: Location[], listType: string) => (
    <div className={`location-map__circle-pin-list location-map__circle-pin-list--${listType}`}>
      {filteredLocationList.map((location) => (
        <div key={location.id} className="location-map__circle-pin-list__item">
          <div 
            className="location-map__circle-pin-list__icon"
            onClick={() => handlePinClick(location.location_code, location.id)}
          >
            <img
              className="location-map__circle-pin-list__icon--img"
              src={circlePinGreenIcon}
              alt="Pin icon"
            />
          </div>
          <div className="location-map__circle-pin-list__label">{location.location_code}</div>
          {selectedLocation === location.location_code && (
            <div className="location-map__dropdown">
              <div className="location-map__dropdown-section">
                <h4 className="location-map__dropdown-title">Displays</h4>
                <div className="location-map__dropdown-list">
                {getLocationDisplays(location.location_code).map(display => (
                  <div key={display.id} className="location-map__dropdown-item">
                    Display
                    {' - '}
                    {`id: ${display.id}`}
                  </div>
                ))}
                </div>
              </div>
              <div className="location-map__dropdown-section">
                <h4 className="location-map__dropdown-title">Labels</h4>
                <div className="location-map__dropdown-list">
                {getLocationScreens(location.location_code).map(screen => (
                  <div key={screen.id} className="location-map__dropdown-item">
                    {`${screen.screenSize}"`}
                    {' - '}
                    {`id: ${screen.id}`}
                  </div>
                ))}
                </div>
              </div>
              <div className="location-map__dropdown-section">
                <h4 className="location-map__dropdown-title">Sensors</h4>
                <div className="location-map__dropdown-list">
                  {getLocationSensors(location.location_code).map(sensor => (
                    <div key={sensor.id} className="location-map__dropdown-item">
                      {sensor.sensor_type}
                      {' - '}
                      {`id: ${sensor.id}`}
                    </div>
                  ))}
                </div>
              </div>
              <button className="location-map__dropdown-btn" onClick={handleDeleteLocation}>Delete Location</button> 
            </div>
          )}
        </div>
      ))}
      
      {/* Render single placeholder for each category if under limit */}
      {(() => {
        // Define limits for each category
        const limits = { 'A': 4, 'B': 5, 'C': 3 };
        const currentCount = filteredLocationList.length;
        const limit = limits[listType as keyof typeof limits];
        
        // Show one placeholder only if current count is under the limit
        if (currentCount < limit) {
          const suggestedCode = `${listType}-${currentCount + 1}`;
          return (
            <div key={`placeholder-${listType}`} className="location-map__circle-pin-list__item location-map__circle-pin-list__item--placeholder">
              <div 
                className="location-map__circle-pin-list__icon location-map__circle-pin-list__icon--placeholder" 
                onClick={() => handleAddLocationClick(suggestedCode)}
              >
                <img
                  className="location-map__circle-pin-list__icon--img"
                  src={circlePlusPlaceholderIcon}
                  alt="Add location placeholder"
                />
              </div>
              <div className="location-map__circle-pin-list__label location-map__circle-pin-list__label--placeholder">
                {suggestedCode}
              </div>
              {showAddLocationModal && clickedPlaceholder === suggestedCode && (
                <div className="location-map__dropdown">
                  <div className="location-map__form-header">
                    <p className='location-map__form-header--label'>Location Code:</p>
                    <img src={closeIcon} alt="Close" className='location-map__form-header--close' onClick={handleCancelAddLocation}/>
                  </div>
                  <input
                    id="locationCode"
                    type="text"
                    value={newLocationCode}
                    className='location-map__form-input'
                    onChange={(e) => setNewLocationCode(e.target.value)}
                    placeholder="Enter location code (e.g., B-21)"
                    autoFocus
                  />
                  
                  <div className="location-map__form-footer">
                    <button 
                      type="button" 
                      onClick={handleAddLocation}
                      className="location-map__form-btn location-map__form-btn--confirm"
                      disabled={isSubmitting || !newLocationCode.trim()}
                    >
                      {isSubmitting ? 'Adding...' : 'Add Location'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        }
        
        // Return null if at or over limit
        return null;
      })()}
    </div>
  );

  return (
    <div className="location-map">
      {/* <div className="location-map__tools">
        <div className="location-map__tool">
          <img className="location-map__tool--icon" src={dragPanIcon} alt="" />
        </div>
        <div className="location-map__tool">
          <img className="location-map__tool--icon" src={gearIcon} alt="" />
        </div>
      </div> */}
      <div className="location-map__map">
        {renderLocationList(locationsA, 'A')}   
        {renderLocationList(locationsB, 'B')} 
        {renderLocationList(locationsC, 'C')}
      </div>
    </div>
  );
};